import { Event } from '../../../config/Registration';

export const scheduleDateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const tallinnParts = (value: string) => {
  const date = new Date(value.endsWith('Z') ? value : `${value}Z`);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Tallinn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date).reduce<Record<string, string>>((parts, part) => {
    parts[part.type] = part.value;
    return parts;
  }, {});
};

export const eventDateKey = (value?: string): string => {
  if (!value) return '';
  const parts = tallinnParts(value);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const eventTimeLabel = (value: string): string => {
  const parts = tallinnParts(value);
  return `${parts.hour}:${parts.minute}`;
};

export const eventTimeMinutes = (value?: string): number => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const parts = tallinnParts(value);
  return Number(parts.hour) * 60 + Number(parts.minute);
};

export const eventOccursOnDate = (event: Event, selectedDate: Date): boolean => {
  const selectedKey = scheduleDateKey(selectedDate);
  const scheduledDateKeys = (event.programTimes || [])
    .map(program => eventDateKey(program.startTime))
    .filter(Boolean);
  if (scheduledDateKeys.length) {
    return scheduledDateKeys.includes(selectedKey);
  }

  const startKey = eventDateKey(event.startTime);
  const endKey = eventDateKey(event.endTime) || startKey;
  return Boolean(startKey && selectedKey >= startKey && selectedKey <= endKey);
};

export const bookedEventDateKeys = (event: Event): string[] => {
  const startKey = eventDateKey(event.startTime);
  return startKey ? [startKey] : [];
};

interface BookingHistoryEntry {
  time: Date | string;
  credit?: boolean;
  bookingType?: string;
}

export const buildBookedDateKeys = (
  bookingHistory: BookingHistoryEntry[],
  events: Event[]
): Set<string> => {
  const keys = new Set<string>();

  bookingHistory.forEach(booking => {
    // Event purchase history can contain multiple dated rows for one event. Event
    // indicators are derived from purchasedTickets below so only its true start
    // date is marked.
    if (booking.credit || booking.bookingType !== 'classes') return;
    const value = booking.time instanceof Date ? booking.time.toISOString() : booking.time;
    const key = eventDateKey(value);
    if (key) keys.add(key);
  });

  events.forEach(event => {
    if (!event.purchasedTickets?.length) return;
    bookedEventDateKeys(event).forEach(key => keys.add(key));
  });

  return keys;
};

export const eventPriceLabel = (event: Event): string => {
  const prices = (event.tickets || []).map(ticket => ticket.effectiveCoinsPrice);
  if (!prices.length) return '';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? `Ȼ ${min}` : `Ȼ ${min}–${max}`;
};
